import type {Dispatch, FunctionComponent, ReactElement, SetStateAction, SyntheticEvent} from 'react'
import {useCallback, useRef, useState} from 'react'
import {useRouteLoaderData} from '@remix-run/react'
import {useInView} from 'react-intersection-observer'
import clsx from 'clsx'
import type {AffiliateLink, InViewRefCardProps, OtherProduct, RootLoaderResponse} from '~/types/index.js'
import {getTranslation} from '~/localization/translation'
import {BrgIconArrowBack} from '~/icons/brg-icon-arrow-back'
import {BrgIconArrowForward} from '~/icons/brg-icon-arrow-forward'
import {useReportCustomEvent, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {Vector} from '~/icons/vector-bottom-recommended-widget.js'
import {getDomainConfig} from '~/domains/index.js'
import RecommendedProductsCard from './recommended-products-card'

const CARDS_GAP_PIXELS = 16

interface RecommendedProductsProps {
  products: OtherProduct[]
  handleCardClick: (
    _e: SyntheticEvent,
    index: number,
    affiliateLink: AffiliateLink,
    affiliateLinkWithClickId: string,
    itemName: string,
    redirectUrl: string,
    elementName: string,
    elementPlace: string,
  ) => void
  shouldForceTrackAnalytics: boolean
  trackSearchParams: string
  clickId: string
}

export const RecommendedProducts: FunctionComponent<RecommendedProductsProps> = ({
  products,
  handleCardClick,
  shouldForceTrackAnalytics,
  trackSearchParams,
  clickId,
}) => {
  const {language, domain, locale} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)
  const [refItems, setRefItems] = useState<InViewRefCardProps>({
    firstItemInView: true,
    lastItemInView: false,
  })
  const reportCustomEvent = useReportCustomEvent()

  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'css-recommended-products',
    widget_place: 'bottom',
  })

  const carouselRef = useRef<HTMLDivElement>(null)
  const firstCardElementRef = useRef<HTMLDivElement>(null)
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastScrollDirectionRef = useRef<'left' | 'right' | null>(null)
  const lastScrollLeftRef = useRef<number>(0)

  const handleCarouselScroll = useCallback(() => {
    const carousel = carouselRef.current
    if (!carousel) return

    const currentScrollLeft = carousel.scrollLeft
    const scrollDirection = currentScrollLeft > lastScrollLeftRef.current ? 'right' : 'left'
    lastScrollLeftRef.current = currentScrollLeft

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (scrollDirection !== lastScrollDirectionRef.current) {
        lastScrollDirectionRef.current = scrollDirection
      }
      reportCustomEvent('carousel_scroll', {
        widget_name: 'css-recommended-products',
        widget_place: 'bottom',
        scroll_direction: scrollDirection,
      })
    }, 150)
  }, [reportCustomEvent])

  const handleScroll = (direction: 'back' | 'forward') => {
    const scrollLeft = carouselRef.current?.scrollLeft ?? 0
    const cardWidth = firstCardElementRef.current?.getBoundingClientRect().width ?? 0
    if (cardWidth) {
      carouselRef.current?.scroll({
        left:
          direction === 'back' ? scrollLeft - cardWidth - CARDS_GAP_PIXELS : cardWidth + CARDS_GAP_PIXELS + scrollLeft,
        behavior: 'smooth',
      })
    }
  }
  const displayWidget = products && products?.length > 1

  return (
    displayWidget && (
      <div className="content-container mx-auto">
        <div
          ref={widgetRef}
          className={clsx(
            'relative isolate flex min-w-full flex-col overflow-x-hidden rounded-3xl py-3 pl-8 tablet:pl-4',
            stylesBydomain.LISTPAGE_WIDGETS_RECOMMENDED_PRODUCTS,
            'listpage_widgets_recommended_products',
          )}
        >
          <Vector className="vector-bottom absolute bottom-0 left-0" />
          <Vector className="vector-top absolute right-0 top-0 rotate-180 mobile:hidden" />
          <div className="mb-2 mt-5 flex justify-between">
            <h3 className="z-10 mb-1 text-2xl font-bold leading-5">{translations.ProductPage.recommendedProducts}</h3>
            <div className="top-1 z-10 flex flex-row gap-2 pr-8 mobile:hidden">
              <button
                aria-label="show previous items"
                disabled={refItems.firstItemInView}
                data-testid="previous-button"
                type="button"
                onClick={() => handleScroll('back')}
                className={clsx(
                  'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-neutral-300 stroke-[0.01em] disabled:pointer-events-none disabled:bg-white [&>svg>g>path]:disabled:fill-neutral-200 [&>svg>path]:disabled:fill-neutral-200',
                  `${stylesBydomain.LISTPAGE_BUTTON_ARROW_BG} listpage_button_arrow_bg`,
                  `${stylesBydomain.LISTPAGE_BUTTON_ARROW_ICON_FILL} listpage_button_arrow_icon_fill`,
                  `${stylesBydomain.LISTPAGE_BUTTON_ARROW_ICON_STROKE} listpage_button_arrow_icon_stroke`,
                )}
              >
                <BrgIconArrowBack />
              </button>
              <button
                aria-label="show next items"
                type="button"
                data-testid="next-button"
                disabled={refItems.lastItemInView}
                onClick={() => handleScroll('forward')}
                className={clsx(
                  'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-neutral-300 stroke-[0.01em] disabled:pointer-events-none disabled:bg-white [&>svg>g>path]:disabled:fill-neutral-200 [&>svg>path]:disabled:fill-neutral-200',
                  `${stylesBydomain.LISTPAGE_BUTTON_ARROW_BG} listpage_button_arrow_bg`,
                  `${stylesBydomain.LISTPAGE_BUTTON_ARROW_ICON_FILL} listpage_button_arrow_icon_fill`,
                  `${stylesBydomain.LISTPAGE_BUTTON_ARROW_ICON_STROKE} listpage_button_arrow_icon_stroke`,
                )}
              >
                <BrgIconArrowForward />
              </button>
            </div>
          </div>
          <div
            id="carousel"
            className="no-scrollbar flex gap-4 overflow-x-auto p-2"
            ref={carouselRef}
            onScroll={handleCarouselScroll}
          >
            {products?.map((deal, i) => {
              return (
                <InViewComponent
                  key={`${deal.asin}-${i}}`}
                  setRefItems={setRefItems}
                  itemIndex={i === 0 ? 0 : i === products.length - 1 ? 1 : undefined}
                >
                  <RecommendedProductsCard
                    productInfo={deal}
                    index={i}
                    ref={i === 0 ? firstCardElementRef : null}
                    handleCardClick={handleCardClick}
                    shouldForceTrackAnalytics={shouldForceTrackAnalytics}
                    trackSearchParams={trackSearchParams}
                    clickId={clickId}
                  />
                </InViewComponent>
              )
            })}
          </div>
        </div>
      </div>
    )
  )
}

function InViewComponent({
  itemIndex,
  setRefItems,
  children,
}: {
  itemIndex: number | undefined
  setRefItems: Dispatch<SetStateAction<InViewRefCardProps>>
  children: ReactElement
}) {
  const {ref} = useInView({
    threshold: 0.9,
    onChange: (inView) => {
      if (itemIndex === 0) {
        setRefItems((prev) => ({...prev, firstItemInView: inView}))
      }
      if (itemIndex === 1) setRefItems((prev) => ({...prev, lastItemInView: inView}))
    },
  })

  return <div ref={ref}>{children}</div>
}
